// ink-component
import React, { ReactElement, ReactNode, MouseEvent, useContext, useEffect } from 'react';
import styled, { ThemeContext, css } from 'styled-components';
import debounce from 'lodash/debounce';

import useClickOutside from '../../hooks/useClickOutside';
import getGlobalProps from '../../helpers/constants/global-props';

import { inputWidths } from '../_common/Controller/styles';
import useDoppelganger, { renderFloatingBox } from '../_common/hooks/useDoppelganger';

import NewTableContext from '../NewTable/NewTableContext';
import TableContext from '../Table/TableContext/TableContext';

import DropdownContext from './contexts/DropdownContext';
import DropdownAnchor from './components/DropdownAnchor/DropdownAnchor';
import DropdownBelt from './components/DropdownBelt/DropdownBelt';
import DropdownBox from './components/DropdownBox/DropdownBox';
import DropdownButton from './components/DropdownButton/DropdownButton';
import DropdownCheckbox from './components/DropdownCheckbox/DropdownCheckbox';
import DropdownHeader from './components/DropdownHeader/DropdownHeader';
import DropdownItem from './components/DropdownItem/DropdownItem';
import DropdownRadio from './components/DropdownRadio/DropdownRadio';
import DropdownSearch from './components/DropdownSearch/DropdownSearch';
import DropdownSeparator from './components/DropdownSeparator/DropdownSeparator';
import DropdownTooltip from './components/DropdownTooltip/DropdownTooltip';
import DropdownTrigger from './components/DropdownTrigger/DropdownTrigger';

import useCloseOnScroll from '../../hooks/useCloseOnScroll';
import { useListKeyboardNavigation } from '../../hooks/useListKeyboardNavigation';

export interface DropdownProps {
  /** Trigger - usually a [Dropdown.Trigger](/components/Dropdown.Trigger/) - as a render prop */
  trigger: (props: { onClick: (event: MouseEvent) => void; activeTrigger: boolean }) => ReactElement;
  /** List of [Dropdown.Item](/components/Dropdown.Item/) */
  children?: ReactNode;
  /** Alignment of the Dropdown container in relation to the trigger */
  align?: 'left' | 'right';
  /** Whether the dropdown window is scrollable or not */
  scrollable?: boolean;
  /** Prevents the window from closing after a click inside the Dropdown container */
  preventClose?: boolean;
  /** Avoid unless absolutely necessary. Overrides Dropdown container's automatic width. Use `max-content` for arbitrarily sized content */
  width?: string;
  /** Marks the element with a given id to make it easier to select it on tests */
  'data-testid'?: string | number;

  /** @ignore */
  portalContainer?: React.RefObject<HTMLDivElement>;
}

export const DropdownWrapper = styled.div`
  display: inline-flex;
  position: relative;

  ${({ theme }) =>
    theme.dropdownScrollTable &&
    css`
      position: static;
    `}
  ${({ theme }) =>
    theme.dropdownIsTypeahead &&
    css`
      ${({ theme }) => theme.width !== 'half' && 'display:  block;'}
      width: ${({ theme }) => inputWidths[theme.width || 'full']};
    `};
`;

const globalProps = getGlobalProps({});

/**
 * @purpose Reveals a set of custom options from a trigger
 * @group Dropdown
 * @categories navigation
 * @aka menu
 */
export function Dropdown(props: DropdownProps) {
  const [open, setOpen, ref, self] = useClickOutside();
  const { tableId, hasScroll } = useContext(TableContext);
  const { scrollableWrapperRef } = useContext(NewTableContext);
  const themeContext = useContext(ThemeContext);

  const close = () => setOpen(false);

  const { 'data-testid': dataTestId } = props;

  const alternatePortalContainer = props.portalContainer?.current;

  const doppelganger = useDoppelganger(ref, scrollableWrapperRef, open, alternatePortalContainer);

  useCloseOnScroll(open, setOpen, scrollableWrapperRef?.current);
  useListKeyboardNavigation({ listElementRef: ref, childrenWrapperRef: self, close, isOpen: open });

  // Close on Scroll for legacy tables.
  useEffect(() => {
    let scrollWrapper;
    const closeOnScroll = () => {
      // Adds event listener to close in the case of a horizontal scroll (with a delay)
      scrollWrapper.addEventListener(
        'scroll',
        debounce(() => {
          setOpen(false);
        }, 250),
      );
    };
    /* Dropdowns should close whenever the Table they are inside scrolls horizontally. */
    if (tableId && hasScroll && open) {
      const table = document.getElementById(tableId);
      scrollWrapper = table && table.closest('.wrapper-ink-table--scroll');
      if (scrollWrapper) closeOnScroll();
    }
  }, [tableId, hasScroll, open, setOpen]);

  return (
    <DropdownContext.Provider value={{ close, preventClose: props.preventClose }}>
      <DropdownWrapper
        ref={ref}
        data-testid={dataTestId}
        theme={{
          ...themeContext,
          dropdownScrollTable: Boolean(tableId && hasScroll),
          dropdownActive: open,
        }}
        {...globalProps}
      >
        <props.trigger
          onClick={(event: MouseEvent) => {
            if (!event) {
              throw new Error(
                `When extending this component's onClick handler, you need to forward the event to the original handler.`,
              );
            }

            event.stopPropagation();
            setOpen(!open);
          }}
          aria-expanded={open}
          activeTrigger={open}
        />
        {open &&
          renderFloatingBox(
            doppelganger,
            <DropdownBox
              theme={{
                ...themeContext,
                dropdownWidth: props.width,
                dropdownScrollable: props.scrollable,
                dropdownAlign: props.align,
                dropdownScrollTable: Boolean(tableId && hasScroll),
                dropdownActive: open,
              }}
              ref={self}
              data-testid={dataTestId ? `${dataTestId}-box` : undefined}
            >
              {props.children}
            </DropdownBox>,
          )}
      </DropdownWrapper>
    </DropdownContext.Provider>
  );
}

Dropdown.Anchor = DropdownAnchor;
Dropdown.Belt = DropdownBelt;
Dropdown.Button = DropdownButton;
Dropdown.Checkbox = DropdownCheckbox;
Dropdown.Item = DropdownItem;
Dropdown.Header = DropdownHeader;
Dropdown.Radio = DropdownRadio;
Dropdown.Search = DropdownSearch;
Dropdown.Separator = DropdownSeparator;
Dropdown.Tooltip = DropdownTooltip;
Dropdown.Trigger = DropdownTrigger;

export { DropdownAnchorProps } from './components/DropdownAnchor/DropdownAnchor';
export { DropdownBeltProps } from './components/DropdownBelt/DropdownBelt';
export { DropdownButtonProps } from './components/DropdownButton/DropdownButton';
export { DropdownCheckboxProps } from './components/DropdownCheckbox/DropdownCheckbox';
export { DropdownItemProps } from './components/DropdownItem/DropdownItem';
export { DropdownHeaderProps } from './components/DropdownHeader/DropdownHeader';
export { DropdownRadioProps } from './components/DropdownRadio/DropdownRadio';
export { DropdownSearchProps } from './components/DropdownSearch/DropdownSearch';
export { DropdownSeparatorProps } from './components/DropdownSeparator/DropdownSeparator';
export { DropdownTriggerProps } from './components/DropdownTrigger/DropdownTrigger';
export { DropdownTooltipProps } from './components/DropdownTooltip/DropdownTooltip';

Dropdown.defaultProps = {
  children: undefined,
  align: 'left',
  scrollable: false,
  preventClose: false,
  'data-testid': undefined,
};

Dropdown.displayName = 'Dropdown';

export default Dropdown;
